import React, { useEffect } from 'react';
import { WelcomePageLayout } from '@/components/cross-entity/onboarding';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star, Users, Building2, Zap, Phone, Calendar, MessageSquare } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pricingAnalytics } from '@/utils/entities/clients/pricingTracking';
import { Badge } from '@/components/ui/badge';

export const ContactSalesWelcome: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Ensure page starts at the top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContinue = () => {
    // Track analytics
    pricingAnalytics.trackContactSales('contact_sales_welcome_page');

    // Preserve add-ons from URL and navigate to sales qualification
    const addonsParam = searchParams.get('addons');
    const qualificationUrl = addonsParam
      ? `/onboarding/sales-qualification?addons=${encodeURIComponent(addonsParam)}`
      : '/onboarding/sales-qualification';

    // Use direct navigation for reliability
    window.location.href = qualificationUrl;
  };

  return (
    <WelcomePageLayout tier="custom" onContinue={handleContinue}>
      <div className="space-y-8">
        {/* Custom Package Badge */}
        <div className="text-center">
          <Badge className="bg-linear-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 text-lg font-semibold mb-4">
            <Star className="w-5 h-5 mr-2" />
            Custom Enterprise Solution
          </Badge>
        </div>

        {/* What's Next Section */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready for Complete Community Transformation?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Perfect! Our sales team specializes in creating custom solutions that go beyond our
            standard packages. Let's discuss your community's unique vision and build something
            extraordinary together.
          </p>
        </div>

        {/* Contact Options */}
        <div className="flex justify-center">
          <div className="grid md:grid-cols-2 gap-6 mb-12 max-w-2xl">
            {/**
            <Card className="text-center border-purple-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle className="text-lg">Immediate Call</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Speak with a specialist right away
                </p>
                <div className="font-semibold text-purple-600">
                  (555) 123-4567
                </div>
              </CardContent>
            </Card>
            */}

            <Card className="text-center border-purple-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Calendar className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle className="text-lg">Schedule Meeting</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Book a convenient time for detailed discussion
                </p>
                <div className="mt-4 flex justify-center">
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => {
                      window.location.href = '/schedule-demo';
                    }}
                  >
                    Schedule Demo
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="text-center border-purple-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle className="text-lg">Detailed Assessment</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">Complete our comprehensive form first</p>
                <div className="font-semibold text-blue-600">Recommended Approach</div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Custom Package Possibilities */}
        <div className="bg-linear-to-r from-purple-50 to-indigo-50 rounded-2xl p-8 border border-purple-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
            Custom Package: Unlimited Possibilities
          </h3>
          <div className="text-center mb-6">
            <Badge className="bg-linear-to-r from-indigo-500 to-purple-500 text-white">
              COMPLETELY CUSTOMIZABLE
            </Badge>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4">
              <div className="bg-purple-100 rounded-full p-3">
                <Building2 className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Custom Banking Integrations</h4>
                <p className="text-sm text-gray-600">
                  Connect with your preferred financial institutions and create bespoke lending
                  solutions
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-indigo-100 rounded-full p-3">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Advanced Governance Systems</h4>
                <p className="text-sm text-gray-600">
                  Multi-ballot voting, complex approval workflows, and automated implementation
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-green-100 rounded-full p-3">
                <Zap className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Municipal Bond Capabilities</h4>
                <p className="text-sm text-gray-600">
                  Large-scale infrastructure financing and community development projects
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-orange-100 rounded-full p-3">
                <Star className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Dedicated Development Team</h4>
                <p className="text-sm text-gray-600">
                  Your own development resources for ongoing customization and enhancement
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* What Makes Custom Different */}
        <div className="bg-linear-to-r from-yellow-50 to-orange-50 border border-orange-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-3">Why Choose Custom?</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>
              • <strong>Unique Requirements:</strong> Your community has specific needs that don't
              fit standard packages
            </li>
            <li>
              • <strong>Complex Governance:</strong> Multiple stakeholder groups or special voting
              requirements
            </li>
            <li>
              • <strong>Large Scale:</strong> Major infrastructure projects or significant financial
              management needs
            </li>
            <li>
              • <strong>Integration Needs:</strong> Must work with existing systems or specific
              vendor relationships
            </li>
            <li>
              • <strong>Growth Planning:</strong> Long-term vision that requires scalable, evolving
              solutions
            </li>
          </ul>
        </div>

        {/* Sales Process Expectations */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-3">Our Sales Process:</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>
              • <strong>Discovery Call:</strong> 45-60 minutes to understand your complete vision
            </li>
            <li>
              • <strong>Technical Assessment:</strong> Detailed analysis of your current systems and
              requirements
            </li>
            <li>
              • <strong>Custom Proposal:</strong> Tailored solution with detailed implementation
              timeline
            </li>
            <li>
              • <strong>Stakeholder Presentations:</strong> We'll present to your board and
              community leadership
            </li>
            <li>
              • <strong>Pilot Planning:</strong> If appropriate, we'll design a paid pilot program
              to validate the approach
            </li>
          </ul>
        </div>

        {/* Important Note */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-3">Please Note:</h3>
          <p className="text-sm text-gray-600">
            Custom solutions require significant planning and development resources. All custom
            work, including pilots, assessment, and implementation, are paid services. We'll provide
            transparent pricing and clear scope boundaries for any engagement.
          </p>
        </div>
      </div>
    </WelcomePageLayout>
  );
};
